import { Cache, FileLoader, ImageLoader } from 'three/webgpu';

import { DEFAULT_SAMPLE, cacheSample, loadSample } from '../audio/synth';
import { CUBE_URLS, MODELS } from '../gfx/assets';
import { useLoadStore, type LoadStep } from '../store/loadStore';
import { SAMPLE_SELECTIONS } from '../store/panelTypes';

/**
 * three's own loader cache, keyed by URL. Filling it here is what lets the
 * scene's loaders parse the files this module fetched instead of asking for
 * them a second time — the preload is the only trip to the network.
 */
Cache.enabled = true;

/** The slots that have a file behind them; the user's own recording has none. */
const SAMPLE_PATHS = SAMPLE_SELECTIONS.filter((path) => path !== '');

/** A load that reports how far along it is, 0 to 1. */
type Task = (onProgress: (ratio: number) => void) => Promise<unknown>;

const ratioOf = (event: ProgressEvent) => (event.total > 0 ? event.loaded / event.total : 0);

/** Pulls a model into the cache `GLTFLoader` reads, byte by byte. */
function loadModel(url: string): Task {
  const loader = new FileLoader().setResponseType('arraybuffer');
  return (onProgress) => loader.loadAsync(url, (event) => onProgress(ratioOf(event)));
}

/**
 * Pulls one cubemap face into the cache `CubeTextureLoader` reads. Images give
 * no progress of their own, so a face counts as one step rather than a
 * percentage — at six small files that reads the same on the bar.
 */
function loadFace(url: string): Task {
  const loader = new ImageLoader();
  return () => loader.loadAsync(url);
}

/** Fetches a sample and hands it to the synth to decode and keep. */
function loadSampleFile(path: string): Task {
  return async (onProgress) => {
    const response = await fetch(path);
    if (!response.ok) throw new Error(`${path}: ${response.status} ${response.statusText}`);

    const total = Number(response.headers.get('content-length') ?? 0);
    if (!response.body || total === 0) {
      await cacheSample(path, await response.arrayBuffer());
      return;
    }

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.byteLength;
      onProgress(Math.min(1, loaded / total));
    }

    const bytes = new Uint8Array(loaded);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    }
    await cacheSample(path, bytes.buffer);
  };
}

/**
 * Runs a batch of loads together and reports them as a single step.
 *
 * A failure counts as done rather than stranding the loader: a missing sample
 * costs that one slot its sound, and a missing model is the scene's problem to
 * report, but neither is a reason to leave the user on a bar that never fills.
 */
async function runStep(step: LoadStep, tasks: Task[]): Promise<void> {
  const { setStep } = useLoadStore.getState();
  const ratios = new Array<number>(tasks.length).fill(0);

  const report = () => setStep(step, ratios.reduce((sum, ratio) => sum + ratio, 0) / tasks.length);

  await Promise.all(
    tasks.map((task, index) =>
      task((ratio) => {
        ratios[index] = ratio;
        report();
      })
        .catch(() => {})
        .finally(() => {
          ratios[index] = 1;
          report();
        }),
    ),
  );

  setStep(step, 1);
}

async function run(): Promise<void> {
  await Promise.all([
    runStep('assets', [
      ...Object.values(MODELS).map(loadModel),
      ...CUBE_URLS.map(loadFace),
    ]),
    runStep('samples', SAMPLE_PATHS.map(loadSampleFile)),
  ]);

  // Decoded during the step above, so this is a buffer copy rather than a round
  // trip: the instrument has a voice the moment it appears.
  await loadSample(DEFAULT_SAMPLE).catch(() => {});

  useLoadStore.getState().assetsLoaded();
}

let running: Promise<void> | null = null;

/**
 * Fetches everything the instrument is made of before any of it is shown: the
 * models and the room cubemap into the loader cache the scene reads from, and
 * every sample all the way through to a decoded buffer, so switching slots
 * later costs nothing.
 *
 * Safe to call more than once — React mounts an effect twice in development,
 * and the second call joins the first rather than starting again.
 */
export function preload(): Promise<void> {
  running ??= run();
  return running;
}

/**
 * Lets go of the fetched files once the scene has been built out of them.
 *
 * Everything in there is raw source — glTF bytes and cubemap images — that has
 * already become a scene graph and a GPU texture, and nothing asks for it
 * again, so it is a couple of megabytes held for nothing past this point.
 */
export function releaseLoadCache(): void {
  Cache.clear();
  Cache.enabled = false;
}
